import type { SearchResult } from '@backrs_ui/common-ui';
import { transformSearchResultsToNuggets } from '@backrs_ui/common-ui';

import { toAgentContextNugget } from '@backrs_ui/grow-ui/src/agentContext/toAgentContextNugget';
import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';

import type { SearchCategory, SearchPageCategory, SearchParentCategory } from '../search.types';

export const useAgentContext = ({
  currentPageCategory,
  currentSearchTerm,
  selectedFilters,
  resultCount,
  currentPage,
  totalPages,
  isSearching,
  availableFilterGroups,
  searchResults,
}: {
  currentPageCategory: SearchPageCategory;
  currentSearchTerm: string;
  selectedFilters: SearchCategory[];
  resultCount: number | null;
  currentPage: number;
  totalPages: number;
  isSearching: boolean;
  availableFilterGroups: SearchParentCategory[];
  searchResults?: SearchResult;
}) => {
  const { nuggets } = searchResults
    ? transformSearchResultsToNuggets(searchResults.results, true)
    : { nuggets: [] };

  // Several groups label an option "All", so a bare label names no dimension.
  const labelByKey = new Map(
    availableFilterGroups.flatMap((group) =>
      group.categories.flatMap((category) => [
        [category.key, `${group.name}: ${category.label}`] as const,
        ...(category.subCategories ?? []).map(
          (sub) => [sub.key, `${group.name}: ${sub.label}`] as const,
        ),
      ]),
    ),
  );

  useRegisterAgentContext({
    kind: 'nuggetList',
    description: 'Search results for the current query and filters',
    nuggets: nuggets.map(toAgentContextNugget),
    data: {
      category: currentPageCategory,
      searchTerm: currentSearchTerm || null,
      isSearching,
      // Keys set from the URL can arrive before the categories that name them.
      activeFilters: selectedFilters.map((key) => labelByKey.get(key) ?? key).join(', ') || null,
      // More Filters renders no group headings, so name the options instead.
      availableFilterGroups:
        availableFilterGroups
          .map((group) =>
            group.isInMoreFilters
              ? `${group.name} (in the More Filters menu): ${group.categories
                  .map((category) => category.label)
                  .join(' / ')}`
              : group.name,
          )
          .join('; ') || null,
      resultCount,
      currentPage,
      totalPages,
    },
  });
};
